import React, {useEffect, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {
    backgroundColor,
    calculateRemSize,
    color,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    h100,
    mb,
    mr,
    mt,
    onDesktop,
    p,
    pl,
    textAlign,
    w50,
    w100
} from "@pg-design/helpers-css";
import {CarOutlineIcon, ClockIcon, InfoIcon, PoiBusIcon, SearchIcon} from "@pg-design/icons-module";
import {Components, IOption, OptionProps, Select, SingleValueProps, ValueContainerProps} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {IFormFieldProps} from "@pg-mono/form";
import {getMainCityBySelectedRegions} from "@pg-rp/interactive-regions-map";

import {setTravelTimeData} from "../actions/set_travel_time";
import {getTravelTimeDistance} from "../hooks/get_travel_time_distance";
import {useTravelTime} from "../hooks/use_travel_time";
import {hitTravelTime} from "../tracking/hit_travel_time";
import {SearchTab} from "../types/SearchTab";
import {TransportLabels} from "../types/TransportLabels";
import {TravelTimeTransport} from "../types/TravelTimeTransport";
import {TravelTimeValues} from "../types/TravelTimeValues";
import {ISearchAutocompleteFormValues} from "./search_autocomplete/SearchAutocomplete";
import {ISearchInputValue} from "./ISearchInputValue";

interface IProps {
    onClose: () => void;
    onCancel: () => void;
    onSubmit: (userQueryParams: {distance: number}) => void;
    getFieldProps: <TFieldName extends keyof ISearchAutocompleteFormValues>(
        fieldName: TFieldName
    ) => IFormFieldProps<TFieldName, ISearchAutocompleteFormValues[TFieldName]>;
    updateTravelTimeState?: typeof setTravelTimeData;
    prevDistance?: number | string;
    placesArrayNotEmpty?: boolean;
    isInModal?: boolean;
}

export const TimeTravelSelects = (props: IProps) => {
    const {transport, time} = useTravelTime();
    const distanceProps = props.getFieldProps("distance");
    const searchProps = props.getFieldProps("search");
    const [prevSearch, setPrevSearch] = useState<ISearchInputValue>();
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        setPrevSearch(searchProps?.value);
        return () => {
            const prevDistance = typeof props.prevDistance === "string" ? parseInt(props.prevDistance) : props.prevDistance;
            distanceProps?.onChange("distance", prevDistance);

            if (searchProps && prevSearch) {
                searchProps.onChange("search", prevSearch);
            }
        };
    }, []);

    useEffect(() => {
        const distance = getTravelTimeDistance(transport.active.value as TravelTimeTransport, time.active.value as TravelTimeValues);
        setIsActive(true);

        if (distanceProps) {
            distanceProps.onChange("distance", distance);
        }

        props.updateTravelTimeState?.({
            transport_type: transport.active.value as TravelTimeTransport,
            travel_time: time.active.value as TravelTimeValues
        });
    }, [transport.active, time.active]);

    const onSearchClick = () => {
        if (isActive) {
            const searchProps = props.getFieldProps("search");
            const distance = getTravelTimeDistance(transport.active.value as TravelTimeTransport, time.active.value as TravelTimeValues);

            hitTravelTime({
                regionName: getRegionName(searchProps),
                travelType: transport.active.label as TransportLabels,
                travelTime: time.active.value as TravelTimeValues
            });

            props.onSubmit({distance: distance});
            props.onClose();
            setIsActive(false);
        }
    };

    return (
        <div css={mt(2)}>
            <div css={optionsHolder}>
                <div css={optionHolder}>
                    <Text variant="body_copy_2" css={mb(0.5)}>
                        {transport.label}
                    </Text>
                    <Select
                        instanceId="transport"
                        options={transport.options}
                        value={transport.active}
                        onChange={transport.onChange}
                        components={{ValueContainer, SingleValue, Option}}
                    />
                </div>

                <div css={optionHolder}>
                    <Text variant="body_copy_2" css={mb(0.5)}>
                        {time.label}
                    </Text>
                    <Select
                        instanceId="time"
                        options={time.options}
                        value={time.active}
                        onChange={time.onChange}
                        components={{ValueContainer, SingleValue, Option}}
                    />
                </div>
            </div>

            <SearchButton variant="filled_primary" size="small" iconLeft={SearchIcon} onClick={onSearchClick} isActive={isActive}>
                szukaj
            </SearchButton>

            <div css={infoHolder}>
                <div css={infoWrapper}>
                    <div css={[h100, mr(1), flex("flex-start", "flex-start")]}>
                        <InfoIcon size="1.5" wrapperSize="1.5" />
                    </div>
                    <Text variant="info_txt_1">
                        Użyj dokładnego adresu Twojej lokalizacji (np. ul. Naruszewicza 27) i ustal maksymalny czas dojazdu do tego miejsca od Twojego
                        przyszłego mieszkania.
                    </Text>
                </div>
            </div>
        </div>
    );
};

// Components
const SingleValue = (props: SingleValueProps) => {
    const option = props.getValue() as IOption[];
    const optionLabel = option[0].label.includes("min") ? "time" : option[0].label;

    return (
        <Components.SingleValue {...props}>
            <div css={pl(0.5)}>
                {optionLabel && getIcon(optionLabel)}
                {props.children}
            </div>
        </Components.SingleValue>
    );
};

const ValueContainer = (props: ValueContainerProps) => {
    return (
        <Components.ValueContainer {...props} css={valueContainerStyle}>
            {props.children}
        </Components.ValueContainer>
    );
};

const Option = (props: OptionProps) => {
    const optionLabel = props.children?.toString().includes("min") ? "time" : props.children?.toString();

    return (
        <Components.Option {...props} css={textAlign("left")}>
            {optionLabel && getIcon(optionLabel)}
            {props.children}
        </Components.Option>
    );
};

const getIcon = (option: string) => {
    switch (option) {
        case TransportLabels.CAR:
            return <CarOutlineIcon size="2" wrapperSize="2" css={mr(1)} />;
        case TransportLabels.PUBLIC_TRANSPORT:
            return <PoiBusIcon size="2" wrapperSize="2" css={mr(1)} />;
        case "time":
            return <ClockIcon size="2" wrapperSize="2" css={mr(1)} />;
        default:
            return null;
    }
};

// Utils
function getRegionName(searchFieldProps: IFormFieldProps<string, ISearchInputValue>) {
    const selectedRegions = searchFieldProps.value.tabType === SearchTab.Regions ? searchFieldProps.value.regions : null;

    if (searchFieldProps.value.tabType === SearchTab.Regions) {
        const mainRegionId = selectedRegions ? getMainCityBySelectedRegions(selectedRegions) : null;
        const mainRegion = mainRegionId ? selectedRegions?.find((region) => region.id === mainRegionId) : null;

        return mainRegion?.short_name || "na";
    }

    if (searchFieldProps.value.tabType === SearchTab.Places) {
        return searchFieldProps.value.place.description;
    }

    return "na";
}

// Styles
const optionsHolder = css`
    ${flexAbsoluteCenter};
    ${flexDirection("column")};
    gap: ${calculateRemSize(1)};
    ${mb(2)};

    ${onDesktop(flexDirection("row"))};
`;

const optionHolder = css`
    ${w100};
    min-width: 170px;

    ${onDesktop(w50)};
`;

const inactiveButtonStyle = (theme: Theme) => css`
    ${w100};
    ${backgroundColor(theme.colors.background[100])};
    ${color(theme.colors.gray[700])};
    cursor: unset;
    border: none;

    &:hover {
        box-shadow: none;
    }
`;

const SearchButton = styled(Button)<{isActive?: boolean}>`
    ${(props) => (props.isActive ? w100 : inactiveButtonStyle(props.theme))};
`;

const infoHolder = (theme: Theme) => css`
    ${mt(2)};

    @media (max-width: ${theme.breakpoints.md}) and (min-width: ${theme.breakpoints.sm}) {
        max-width: 350px;
    }
`;

const infoWrapper = css`
    ${flex("flex-start", "flex-start")};
    ${pl(0.5)};
`;

const valueContainerStyle = css`
    flex: unset;

    @media (max-width: 1445px) and (min-width: 1365px) {
        ${p(0, 0, 0, 0.5)};
    }

    @media (max-width: 1125px) and (min-width: 1075px) {
        ${p(0, 0, 0, 2)};
    }

    @media (max-width: 1075px) and (min-width: 1024px) {
        ${p(0, 0, 0, 0.5)};
    }
`;
import React, {useEffect, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {
    backgroundColor,
    calculateRemSize,
    color,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    h100,
    mb,
    mr,
    mt,
    onDesktop,
    p,
    pl,
    textAlign,
    w50,
    w100
} from "@pg-design/helpers-css";
import {CarOutlineIcon, ClockIcon, InfoIcon, PoiBusIcon, SearchIcon} from "@pg-design/icons-module";
import {Components, IOption, OptionProps, Select, SingleValueProps, ValueContainerProps} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {IFormFieldProps} from "@pg-mono/form";
import {getMainCityBySelectedRegions} from "@pg-rp/interactive-regions-map";

import {setTravelTimeData} from "../actions/set_travel_time";
import {getTravelTimeDistance} from "../hooks/get_travel_time_distance";
import {useTravelTime} from "../hooks/use_travel_time";
import {hitTravelTime} from "../tracking/hit_travel_time";
import {SearchTab} from "../types/SearchTab";
import {TransportLabels} from "../types/TransportLabels";
import {TravelTimeTransport} from "../types/TravelTimeTransport";
import {TravelTimeValues} from "../types/TravelTimeValues";
import {ISearchAutocompleteFormValues} from "./search_autocomplete/SearchAutocomplete";
import {ISearchInputValue} from "./ISearchInputValue";

interface IProps {
    onClose: () => void;
    onCancel: () => void;
    onSubmit: (userQueryParams: {distance: number}) => void;
    getFieldProps: <TFieldName extends keyof ISearchAutocompleteFormValues>(
        fieldName: TFieldName
    ) => IFormFieldProps<TFieldName, ISearchAutocompleteFormValues[TFieldName]>;
    updateTravelTimeState?: typeof setTravelTimeData;
    prevDistance?: number | string;
    placesArrayNotEmpty?: boolean;
    isInModal?: boolean;
}

export const TimeTravelSelects = (props: IProps) => {
    const {transport, time} = useTravelTime();
    const distanceProps = props.getFieldProps("distance");
    const searchProps = props.getFieldProps("search");
    const [prevSearch, setPrevSearch] = useState<ISearchInputValue>();
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        setPrevSearch(searchProps?.value);
        return () => {
            const prevDistance = typeof props.prevDistance === "string" ? parseInt(props.prevDistance) : props.prevDistance;
            distanceProps?.onChange("distance", prevDistance);

            if (searchProps && prevSearch) {
                searchProps.onChange("search", prevSearch);
            }
        };
    }, []);

    useEffect(() => {
        const distance = getTravelTimeDistance(transport.active.value as TravelTimeTransport, time.active.value as TravelTimeValues);
        setIsActive(true);

        if (distanceProps) {
            distanceProps.onChange("distance", distance);
        }

        props.updateTravelTimeState?.({
            transport_type: transport.active.value as TravelTimeTransport,
            travel_time: time.active.value as TravelTimeValues
        });
    }, [transport.active, time.active]);

    const onSearchClick = () => {
        if (isActive) {
            const searchProps = props.getFieldProps("search");
            const distance = getTravelTimeDistance(transport.active.value as TravelTimeTransport, time.active.value as TravelTimeValues);

            hitTravelTime({
                regionName: getRegionName(searchProps),
                travelType: transport.active.label as TransportLabels,
                travelTime: time.active.value as TravelTimeValues
            });

            props.onSubmit({distance: distance});
            props.onClose();
            setIsActive(false);
        }
    };

    return (
        <div css={mt(2)}>
            <div css={optionsHolder}>
                <div css={optionHolder}>
                    <Text variant="body_copy_2" css={mb(0.5)}>
                        {transport.label}
                    </Text>
                    <Select
                        instanceId="transport"
                        options={transport.options}
                        value={transport.active}
                        onChange={transport.onChange}
                        components={{ValueContainer, SingleValue, Option}}
                    />
                </div>

                <div css={optionHolder}>
                    <Text variant="body_copy_2" css={mb(0.5)}>
                        {time.label}
                    </Text>
                    <Select
                        instanceId="time"
                        options={time.options}
                        value={time.active}
                        onChange={time.onChange}
                        components={{ValueContainer, SingleValue, Option}}
                    />
                </div>
            </div>

            <SearchButton variant="filled_primary" size="small" iconLeft={SearchIcon} onClick={onSearchClick} isActive={isActive}>
                szukaj
            </SearchButton>

            <div css={infoHolder}>
                <div css={infoWrapper}>
                    <div css={[h100, mr(1), flex("flex-start", "flex-start")]}>
                        <InfoIcon size="1.5" wrapperSize="1.5" />
                    </div>
                    <Text variant="info_txt_1">
                        Użyj dokładnego adresu Twojej lokalizacji (np. ul. Naruszewicza 27) i ustal maksymalny czas dojazdu do tego miejsca od Twojego
                        przyszłego mieszkania.
                    </Text>
                </div>
            </div>
        </div>
    );
};

// Components
const SingleValue = (props: SingleValueProps) => {
    const option = props.getValue() as IOption[];
    const optionLabel = option[0].label.includes("min") ? "time" : option[0].label;

    return (
        <Components.SingleValue {...props}>
            <div css={pl(0.5)}>
                {optionLabel && getIcon(optionLabel)}
                {props.children}
            </div>
        </Components.SingleValue>
    );
};

const ValueContainer = (props: ValueContainerProps) => {
    return (
        <Components.ValueContainer {...props} css={valueContainerStyle}>
            {props.children}
        </Components.ValueContainer>
    );
};

const Option = (props: OptionProps) => {
    const optionLabel = props.children?.toString().includes("min") ? "time" : props.children?.toString();

    return (
        <Components.Option {...props} css={textAlign("left")}>
            {optionLabel && getIcon(optionLabel)}
            {props.children}
        </Components.Option>
    );
};

const getIcon = (option: string) => {
    switch (option) {
        case TransportLabels.CAR:
            return <CarOutlineIcon size="2" wrapperSize="2" css={mr(1)} />;
        case TransportLabels.PUBLIC_TRANSPORT:
            return <PoiBusIcon size="2" wrapperSize="2" css={mr(1)} />;
        case "time":
            return <ClockIcon size="2" wrapperSize="2" css={mr(1)} />;
        default:
            return null;
    }
};

// Utils
function getRegionName(searchFieldProps: IFormFieldProps<string, ISearchInputValue>) {
    const selectedRegions = searchFieldProps.value.tabType === SearchTab.Regions ? searchFieldProps.value.regions : null;

    if (searchFieldProps.value.tabType === SearchTab.Regions) {
        const mainRegionId = selectedRegions ? getMainCityBySelectedRegions(selectedRegions) : null;
        const mainRegion = mainRegionId ? selectedRegions?.find((region) => region.id === mainRegionId) : null;

        return mainRegion?.short_name || "na";
    }

    if (searchFieldProps.value.tabType === SearchTab.Places) {
        return searchFieldProps.value.place.description;
    }

    return "na";
}

// Styles
const optionsHolder = css`
    ${flexAbsoluteCenter};
    ${flexDirection("column")};
    gap: ${calculateRemSize(1)};
    ${mb(2)};

    ${onDesktop(flexDirection("row"))};
`;

const optionHolder = css`
    ${w100};
    min-width: 170px;

    ${onDesktop(w50)};
`;

const inactiveButtonStyle = (theme: Theme) => css`
    ${w100};
    ${backgroundColor(theme.colors.background[100])};
    ${color(theme.colors.gray[700])};
    cursor: unset;
    border: none;

    &:hover {
        box-shadow: none;
    }
`;

const SearchButton = styled(Button)<{isActive?: boolean}>`
    ${(props) => (props.isActive ? w100 : inactiveButtonStyle(props.theme))};
`;

const infoHolder = (theme: Theme) => css`
    ${mt(2)};

    @media (max-width: ${theme.breakpoints.md}) and (min-width: ${theme.breakpoints.sm}) {
        max-width: 350px;
    }
`;

const infoWrapper = css`
    ${flex("flex-start", "flex-start")};
    ${pl(0.5)};
`;

const valueContainerStyle = css`
    flex: unset;

    @media (max-width: 1445px) and (min-width: 1365px) {
        ${p(0, 0, 0, 0.5)};
    }

    @media (max-width: 1125px) and (min-width: 1075px) {
        ${p(0, 0, 0, 2)};
    }

    @media (max-width: 1075px) and (min-width: 1024px) {
        ${p(0, 0, 0, 0.5)};
    }
`;
